import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Anchor,
  Button,
  Checkbox,
  Divider,
  Grid,
  MultiSelect,
  Paper,
  PasswordInput,
  SegmentedControl,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';
import { getMarketCategoryColor } from '@utils/mappers';

import { CreateCustomerRequestBody } from '@interfaces/customer.interface';

import { useCreateCustomerMutation } from '@api/customer.api';
import { useGetMarketCategoriesQuery } from '@api/marketCategory.api';

import useFormEnhanced from '@hooks/useFormEnhanced';
import useGRecaptcha from '@hooks/useGRecaptcha';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from './common/locations';
import CompanyRoleSelector from './CompanyRoleSelector';
import GoodfellowSelector from './GoodfellowSelector';
import InterestSectorSelector from './InterestSectorSelector';
import TechniqueSelector from './TechniqueSelector';

export default function RegisterForm() {
  // ==========================================================================
  // General
  // ==========================================================================
  const navigate = useNavigate();
  const { getRecaptchaToken } = useGRecaptcha();
  const [searchParams] = useSearchParams();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    email: '',
    emailConfirmation: '',
    password: '',
    passwordConfirmation: '',
    firstName: '',
    lastName: '',
    phone: '',
    company: '',
    companyRoleName: '',
    department: '',
    vatNumber: '',
    laboratory: '',
    address: '',
    postalCode: '',
    city: '',
    province: '',
    region: '',
    country: '',
    website: '',
    interestSector: [],
    techniquesUsed: [],
    goodfellow: [],
    additionalNotes: '',
    allowedCategories: searchParams.get('m')
      ? [searchParams.get('m')!]
      : (['Alimentare'] as string[]),
  };

  const form = useFormEnhanced({
    initialValues,
    validate: {
      email: (val) => (/^\S+@\S+$/.test(val) ? null : 'Email non valida'),
      emailConfirmation: (val, values) =>
        val === values.email ? null : 'Le email non coincidono',
      passwordConfirmation: (val, values) =>
        val === values.password ? null : 'Le password non coincidono',
    },
    normalizeFields: {
      capitalizeFields: ['firstName', 'lastName', 'department', 'laboratory'],
      uppercaseFields: ['vatNumber'],
      lowercaseFields: ['email'],
    },
  });

  // ==========================================================================
  // Api
  // ==========================================================================
  const [createCustomer, { isLoading }] = useCreateCustomerMutation();
  const { data: marketCategories = [], isLoading: isMarketCategoriesLoading } =
    useGetMarketCategoriesQuery();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const data: CreateCustomerRequestBody = {
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone.trim() === '' ? undefined : values.phone,
        company: values.company,
        companyRoleName: values.companyRoleName,
        department:
          values.department.trim() === '' ? undefined : values.department,
        vatNumber: values.vatNumber,
        laboratory:
          values.laboratory.trim() === '' ? undefined : values.laboratory,
        address: values.address,
        postalCode: values.postalCode,
        city: values.city,
        province: values.province,
        region: values.region.trim() === '' ? undefined : values.region,
        country: values.country,
        website: values.website.trim() === '' ? undefined : values.website,
        interestSector: values.interestSector,
        techniquesUsed: values.techniquesUsed,
        goodfellow: values.goodfellow,
        allowedCategories: values.allowedCategories,
        additionalNotes:
          values.additionalNotes.trim() === ''
            ? undefined
            : values.additionalNotes,
      };

      const recaptchaToken = await getRecaptchaToken('SignUp');

      await createCustomer({ body: data, recaptchaToken });

      showNotification({
        title: 'Registrazione avvenuta con successo',
        message: "Controlla la tua casella email per l'attivazione.",
      });

      navigate('/');
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Paper p={{ base: 'sm', sm: 'md' }}>
      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <Grid>
          <Grid.Col
            span={12}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Text size="sm" fw="bold">
              Seleziona il mercato di interesse
            </Text>
            <SegmentedControl
              mt="sm"
              mb="md"
              fullWidth
              data={marketCategories.map((category) => ({
                label: category.name,
                value: category.name,
              }))}
              color={getMarketCategoryColor(form.values.allowedCategories[0])}
              value={form.values.allowedCategories[0]}
              onChange={(value) => {
                form.setFieldValue('allowedCategories', [value]);
              }}
              disabled={isMarketCategoriesLoading}
              style={{ maxWidth: '100%' }}
            />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <TextInput
              type="email"
              label="Email"
              placeholder="email@example.com"
              required
              {...form.getInputProps('email')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              type="email"
              label="Ripeti email"
              placeholder="email@example.com"
              required
              {...form.getInputProps('emailConfirmation')}
            />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <PasswordInput
              label="Password"
              placeholder="La tua password"
              required
              {...form.getInputProps('password')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <PasswordInput
              label="Ripeti password"
              placeholder="La tua password"
              required
              {...form.getInputProps('passwordConfirmation')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Divider label="Informazioni base" labelPosition="left" />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Nome"
              placeholder="Il tuo nome"
              required
              {...form.getInputProps('firstName')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Cognome"
              placeholder="Il tuo cognome"
              required
              {...form.getInputProps('lastName')}
            />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Nome azienda"
              placeholder="Ragione sociale azienda"
              required
              {...form.getInputProps('company')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Partita IVA"
              placeholder="Partita IVA dell'azienda"
              required
              {...form.getInputProps('vatNumber')}
            />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <CompanyRoleSelector {...form.getInputProps('companyRoleName')} />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Telefono"
              placeholder="Il tuo telefono"
              {...form.getInputProps('phone')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Divider label="Indirizzo" labelPosition="left" />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <NationSelector required {...form.getInputProps('country')} />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Regione"
              placeholder="La tua regione"
              {...form.getInputProps('region')}
            />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            {form.values.country === 'Italia' ? (
              <ProvinceSelector required {...form.getInputProps('province')} />
            ) : (
              <TextInput
                label="Provincia"
                placeholder="La tua provincia"
                required
                {...form.getInputProps('province')}
              />
            )}
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            {form.values.country === 'Italia' ? (
              <CitySelector
                province={form.values.province}
                disabled={form.values.province === ''}
                required
                {...form.getInputProps('city')}
              />
            ) : (
              <TextInput
                label="Città"
                placeholder="La tua città"
                required
                {...form.getInputProps('city')}
              />
            )}
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            {form.values.country === 'Italia' ? (
              <ZipSelector
                city={form.values.city}
                disabled={form.values.city === ''}
                required
                {...form.getInputProps('postalCode')}
              />
            ) : (
              <TextInput
                label="CAP"
                placeholder="Il tuo CAP"
                required
                {...form.getInputProps('postalCode')}
              />
            )}
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Indirizzo"
              placeholder="Il tuo indirizzo"
              required
              {...form.getInputProps('address')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Divider label="Informazioni aggiuntive" labelPosition="left" />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <InterestSectorSelector {...form.getInputProps('interestSector')} />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TechniqueSelector {...form.getInputProps('techniquesUsed')} />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <GoodfellowSelector {...form.getInputProps('goodfellow')} />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Laboratorio"
              placeholder="Il tuo laboratorio"
              {...form.getInputProps('laboratory')}
            />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label="Dipartimento"
              placeholder="Il tuo dipartimento"
              {...form.getInputProps('department')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <MultiSelect
              label="Altri mercati di interesse"
              data={marketCategories
                .filter((c) => c.name !== form.values.allowedCategories[0])
                .map((category) => ({
                  label: category.name,
                  value: category.name,
                }))}
              disabled={isMarketCategoriesLoading}
              placeholder={
                form.values.allowedCategories.length !== 1
                  ? undefined
                  : 'Seleziona i mercati di interesse'
              }
              error={form.errors.allowedCategories}
              value={form.values.allowedCategories.slice(1)}
              onChange={(value) => {
                form.setFieldValue('allowedCategories', [
                  form.values.allowedCategories[0],
                  ...value,
                ]);
              }}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Note aggiuntive"
              placeholder="Le tue note aggiuntive"
              {...form.getInputProps('additionalNotes')}
            />
          </Grid.Col>
        </Grid>

        <Checkbox
          mt="xl"
          label={
            <Text size="sm">
              Ho letto e accetto la{' '}
              <Anchor href="#" target="_blank">
                privacy policy
              </Anchor>{' '}
              <Text span c="red">
                *
              </Text>
            </Text>
          }
          required
        />

        {form.errors.general && (
          <Text c="red" size="sm" mt="xl">
            {form.errors.general}
          </Text>
        )}

        <Button type="submit" fullWidth mt="xl" loading={isLoading}>
          Registrati
        </Button>
      </form>
    </Paper>
  );
}
